import type {
  PersonSportRankingResponseRelatedRankingResponseRelatedPagedData,
  PositionResponse,
  StateResponse,
} from '@on3/api';
import { On3IconSoftballLogoFull } from '@on3/icons';
import type { ErrorData } from '@on3/ui-lib/api/schema/custom-contracts';
import { Link } from '@on3/ui-lib/components/Link';
import { useAuth } from '@on3/ui-lib/src/contexts/AuthProvider';
import { useSite } from '@on3/ui-lib/src/contexts/SiteProvider';
import { databaseUrl, externalApi } from '@on3/ui-lib/utils/api';
import { slugify } from '@on3/ui-lib/utils/slugify';
import type { AxiosError } from 'axios';
import { AdminLink } from 'components/User/AdminLink';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';
import { RDBRoutes } from 'utils/routes';

import { Paywall } from '../../Paywall';
import { RankingsSearchLayout } from '../../Search/Layout';
import { PlayerRankingsFilters } from '../PlayerRankingsFilters';
import { PlayerRankingList } from '../PlayerRankingsList';
import { PlayerRankingsNav } from '../PlayerRankingsNav';
import styles from './PlayerRankingsPage.module.scss';

interface PlayerRankingsPageProps
  extends PersonSportRankingResponseRelatedRankingResponseRelatedPagedData {
  filters: {
    years: number[];
    positions: PositionResponse[];
    states: StateResponse[];
  };
  title: string;
}

const arr: [] = [];

export const PlayerRankingsPage = ({
  filters,
  list,
  pagination,
  title,
  relatedModel,
}: PlayerRankingsPageProps) => {
  const { query } = useRouter();
  const { years, positions, states } = filters;
  const filteredStates = states.slice(0, 51);
  const { ranking } = relatedModel || {};
  const { user } = useAuth();
  const isSoftball = ranking?.sportKey === 12;
  const hasSubscription = user?.has;
  const showPaywall = isSoftball && !hasSubscription;
  const payWallList = list?.slice(0, 10) || arr;
  const [pageNumber, setPageNumber] = useState(2);
  const [disable, setDisable] = useState(false);
  const [recruitList, setRecruitList] = useState(
    showPaywall ? payWallList : list || arr,
  );

  const {
    position: positionAbbr,
    rankingType,
    sport,
    state: stateAbbr,
    year,
    type,
  } = query;

  const { sportsData } = useSite();

  const page = pageNumber;

  const isIndustry = rankingType?.includes('industry');

  const isIndustrySport = sportsData?.find(
    (sportData) => sportData.key === ranking?.sportKey,
  )?.isIndustryRankable;

  const canonicalURL = `https://www.on3.com/rivals/rankings/${
    isIndustrySport ? 'industry-player' : 'player'
  }/${sport}/${year}/`;
  const handleClick: React.MouseEventHandler = useCallback(async () => {
    setDisable(true);

    const sportKey = sportsData?.find(
      (sp) => sp?.name && slugify(sp?.name) === sport,
    )?.key;
    const params = {
      consensus: isIndustry ? true : false,
      orderBy: isIndustry ? 'Consensus' : 'On3',
      sportKey,
      year: `${year}`,
      rankingType: 'Player',
      stateAbbr,
      positionAbbr,
      page,
      orgType: type || 'HighSchool',
    };

    try {
      const response = await externalApi.get(RDBRoutes.personSportRankings, {
        params,
      });

      setRecruitList([...recruitList, ...response.data.list]);
      setPageNumber(pageNumber + 1);
    } catch (e: unknown) {
      const { response, message }: AxiosError<ErrorData> =
        e as AxiosError<ErrorData>;

      console.error(
        'DB PlayerRankings Error: ',
        response?.data?.message || message,
      );
    } finally {
      setDisable(false);
    }
  }, [
    isIndustry,
    page,
    pageNumber,
    positionAbbr,
    recruitList,
    sport,
    sportsData,
    stateAbbr,
    type,
    year,
  ]);

  const showLoadMoreButton =
    !showPaywall &&
    pagination?.count &&
    pagination.count > 50 &&
    recruitList?.length < pagination?.count;

  return (
    <RankingsSearchLayout
      positions={positions}
      statesData={filteredStates}
      yearsData={filters.years}
    >
      <div data-name="header">
        <Head>
          <link href={canonicalURL} rel="canonical" />
        </Head>

        <AdminLink path={`${databaseUrl}/rankings/${ranking?.key}/edit/`} />
        <header className={styles.header}>
          <h1>{title}</h1>
          <div className={styles.searchOptions} data-name="list-filters">
            <PlayerRankingsFilters
              positions={positions}
              states={filteredStates}
              years={years}
            />
            {isIndustrySport && <PlayerRankingsNav />}
          </div>
        </header>
        {isSoftball && (
          <div className={styles.softballBlock}>
            <span>Softball Rankings Provided by</span>
            <Link href="/softball/">
              <On3IconSoftballLogoFull height={36} width={80} />
            </Link>
          </div>
        )}
      </div>
      <div className={styles.list} data-name="list" role="table">
        {recruitList.length > 0 && (
          <>
            <PlayerRankingList
              playerList={recruitList}
              relatedModel={relatedModel}
            />
            {showPaywall && <Paywall />}
          </>
        )}
        {!recruitList.length && (
          <p className={styles.noResults}>
            On3 did not produce a ranking for this class.
          </p>
        )}
        {!!showLoadMoreButton && (
          <button
            className={styles.btnLoadMore}
            disabled={disable}
            onClick={handleClick}
          >
            Load More
          </button>
        )}
      </div>
    </RankingsSearchLayout>
  );
};
